/**
 * META — docs/03 §3.1.
 *
 * `GET /health` is listed for completeness and is **never called by the app**
 * (§8.8). It exists for uptime monitors and the status page; `status` and `mode`
 * ride on `GET /config` and on the `heartbeat` channel, which the client already
 * has. It is here so nobody wires it into a client retry loop by accident.
 */

import type { HttpClient, RequestOptions } from '../client'
import type { OkBody, OpQuery } from '../types'

export function metaEndpoints(client: HttpClient) {
  return {
    /**
     * Ten samples spread over ~2s at cold load, **serially** — parallel samples
     * share queueing delay and correlate their error (D9). `lib/clock` owns the
     * sampling policy; this is only the call.
     */
    getServerTime(query?: OpQuery<'getServerTime'>, options?: RequestOptions) {
      return client.get<OkBody<'getServerTime'>>('/time', { ...options, query })
    },

    /**
     * Once at cold load, before the order ticket renders, and again whenever a
     * `heartbeat` frame carries a higher `config_revision`. Every disabled-button
     * reason in the ticket is a field in this payload (§1.6), which is what makes
     * "disabled with a reason" possible instead of "surprise rejection".
     */
    getExchangeConfig(options?: RequestOptions) {
      return client.get<OkBody<'getExchangeConfig'>>('/config', options)
    },

    /** Never called by the app. See the module note. */
    getHealth(options?: RequestOptions) {
      return client.get<OkBody<'getHealth'>>('/health', options)
    },
  }
}

export type MetaEndpoints = ReturnType<typeof metaEndpoints>
